import { Text } from '@janeapp/burrito-design-system';
import classNames from 'classnames';
import { useRef } from 'react';
import { useButton } from 'react-aria';
import { NoPhotoAvatar, OpeningBadge } from '~/components';
import { PractitionersSearchResult } from '~/api';
import { useAppDispatch } from '~/state';
import { setSelectedPractitioner } from '~/state/activeSelection';
import { formatDate } from '~/utilities';

export interface PractitionerItemProps {
  result: PractitionersSearchResult;
}

export const PractitionerItem = ({ result }: PractitionerItemProps) => {
  const dispatch = useAppDispatch();
  const ref = useRef<HTMLLIElement>(null);
  const { buttonProps } = useButton(
    {
      onPress: () => dispatch(setSelectedPractitioner(result)),
      elementType: 'li',
      'aria-label': 'Click to view practitioner details',
    },
    ref
  );

  const { fullName, photo, clinicName, firstOpening } = result;

  const listItemClasses = classNames(
    'border-b',
    'border-neutral-20',
    'cursor-pointer',
    'focus:bg-neutral-20',
    'focus:outline-none',
    'hover:bg-neutral-20',
    'hover:pointer',
    'w-full',
    'px-5',
    'py-4'
  );

  // https://jira-jane.atlassian.net/browse/MPL-1383
  // removed distance indictor for squam pilot
  // const distanceFormatted = useMemo(() => formatDistance(distance), [distance]);

  return (
    <li
      ref={ref}
      {...buttonProps}
      className={listItemClasses}
      role="menuitem"
      data-testid="practitioner-item"
    >
      <div className="flex flex-row gap-4">
        <div className="flex h-20 w-20 min-w-20 items-center justify-center overflow-hidden rounded-full border border-neutral-20 bg-neutral-30">
          {photo && photo.startsWith('https') ? (
            <img
              data-testid="practitioner-photo"
              src={photo}
              alt={`${fullName}'s headshot`}
              className="h-full w-full object-cover"
            />
          ) : (
            <NoPhotoAvatar />
          )}
        </div>
        <div className="flex flex-col justify-center gap-0.5">
          <Text size="lg" className="font-semibold text-brand-80 underline">
            {fullName}
          </Text>
          <div
            data-testid="location-info"
            className="whitespace-normal text-md text-neutral-90"
          >
            <>{clinicName}</>
            {/* • {distanceFormatted} */}
          </div>
          <OpeningBadge
            badgeStatus={
              firstOpening ? formatDate(firstOpening.startAt) : undefined
            }
            className="mt-0.5"
          />
        </div>
      </div>
    </li>
  );
};
